import React, { useState, useCallback, useMemo, useEffect } from 'react';
function App() {
  const [attendanceData, setAttendanceData] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pinAuthenticated, setPinAuthenticated] = useState(false);
  const [pin, setPin] = useState(['', '', '', '', '', '']);
  const [pinError, setPinError] = useState('');
  const [buttonLoading, setButtonLoading] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [registerData, setRegisterData] = useState({
    mobilenumber: '',
    password:'',
    pin: ['', '', '', '', '', ''],
  });
  const [registerError, setRegisterError] = useState('');
  const [registerSuccess, setRegisterSuccess] = useState('');
  const [registerLoading, setRegisterLoading] = useState(false);

  const fetchAttendanceData = useCallback(async (pinValue) => {
    const pinString = pinValue || pin.join('');
    setLoading(true);
    try {
      const attRes = await fetch('https://attendance-server-pi.vercel.app/attendance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: pinString })
      });
      const attData = await attRes.json();
      setAttendanceData(attData);
    } catch (error) {
      console.error('Error fetching attendance data:', error);
    } finally {
      setLoading(false);
    }
  }, [pin]);

  const fetchProfileData = useCallback(async (pinValue) => {
    const pinString = pinValue || pin.join('');
    setLoading(true);
    try {
      const profileRes = await fetch('https://attendance-server-pi.vercel.app/profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: pinString })
      });
      const profileData = await profileRes.json();
      setProfileData(profileData);
    } catch (error) {
      console.error('Error fetching profile data:', error);
    } finally {
      setLoading(false);
    }
  }, [pin]);

  const handlePinChange = useCallback((index, value) => {
    if (value.length > 1) return; // Only allow single digit
    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    setPinError('');
    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`pin-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  }, [pin]);

  const handlePinKeyDown = useCallback((index, e) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      const prevInput = document.getElementById(`pin-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  }, [pin]);

  const handlePinPaste = useCallback((e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    const newPin = pastedData.split('').concat(Array(6).fill('')).slice(0, 6);
    setPin(newPin);
    setPinError('');
  }, []);

  const validatePin = useCallback(async () => {
    const enteredPin = pin.join('');
    if (enteredPin.length !== 6) {
      setPinError('Please enter all 6 digits');
      return;
    }
    setButtonLoading(true);
    setLoading(true);
    try {
      // Call backend to verify PIN
      const verifyRes = await fetch('https://attendance-server-pi.vercel.app/verify-pin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: enteredPin })
      });
      const verifyData = await verifyRes.json();
      if (!verifyRes.ok || !verifyData.valid) {
        setPinError('Invalid PIN. Please try again.');
        setPin(['', '', '', '', '', '']);
        setLoading(false);
        setButtonLoading(false);
        document.getElementById('pin-0')?.focus();
        return;
      }
      setPinAuthenticated(true);
      fetchAttendanceData(enteredPin);
      fetchProfileData(enteredPin);
    } catch (error) {
      setPinError('Error verifying PIN. Please try again.');
      setPin(['', '', '', '', '', '']);
      document.getElementById('pin-0')?.focus();
    } finally {
      setLoading(false);
      setButtonLoading(false);
    }
  }, [pin, fetchAttendanceData, fetchProfileData]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    validatePin();
  }, [validatePin]);

  const handleLogout = useCallback(() => {
    setPinAuthenticated(false);
    setPin(['', '', '', '', '', '']);
    setPinError('');
    setAttendanceData(null);
    setProfileData(null);
  }, []);

  const handleRegisterChange = (field, value) => {
    if (field === 'pin') {
      if (value.length > 1) return;
      const newPin = [...registerData.pin];
      newPin[value.index] = value.digit;
      setRegisterData({ ...registerData, pin: newPin });
      setRegisterError('');
      // Auto-focus next input
      if (value.digit && value.index < 5) {
        const nextInput = document.getElementById(`pin-${value.index + 1}`);
        if (nextInput) nextInput.focus();
      }
    } else {
      setRegisterData({ ...registerData, [field]: value });
      setRegisterError('');
    }
  };

  const handleRegisterPinPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    const newPin = pastedData.split('').concat(Array(6).fill('')).slice(0, 6);
    setRegisterData({ ...registerData, pin: newPin });
    setRegisterError('');
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setRegisterError('');
    setRegisterSuccess('');
    if (registerData.pin.join('').length !== 6) {
      setRegisterError('Please enter a 6-digit PIN');
      return;
    }
    if (!registerData.mobilenumber) {
      setRegisterError('Please enter your mobile number');
      return;
    }
    if (!registerData.password) {
      setRegisterError('Please enter your Netra password');
      return;
    }
    setRegisterLoading(true);
    try {
      const res = await fetch('https://attendance-server-pi.vercel.app/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mobilenumber: registerData.mobilenumber,
          password: registerData.password,
          pin: registerData.pin.join(''),
        })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setRegisterError(data.message || 'Registration failed');
      } else {
        setRegisterSuccess('Registration successful! You can now log in.');
        setTimeout(() => {
          setShowRegister(false);
          setRegisterSuccess('');
        }, 1500);
      }
    } catch (err) {
      setRegisterError('Registration failed. Please try again.');
    } finally {
      setRegisterLoading(false);
    }
  };

  // Memoized helper functions
  const getSessionIcon = useCallback((val) => {
    if (val === '1') return (
      <div className="session-icon present">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
    );
    if (val === '0') return (
      <div className="session-icon absent">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
    );
    return (
      <div className="session-icon neutral">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
    );
  }, []);

  // Memoized computed values
  const computedData = useMemo(() => {
    const dayObjects = attendanceData?.dayObjects || [];
    const twoWeekSessions = attendanceData?.twoWeekSessions || { present: 0, absent: 0, nosessions: 0 };
    const totalPercentage = attendanceData?.totalPercentage || 0;

    const getProgressClass = (percentage) => {
      if (percentage >= 75) return 'progress-high';
      if (percentage >= 50) return 'progress-medium';
      return 'progress-low';
    };

    const getPercentageClass = (percentage) => {
      if (percentage >= 75) return 'percentage-high';
      if (percentage >= 50) return 'percentage-medium';
      return 'percentage-low';
    };

    return {
      dayObjects,
      twoWeekSessions,
      totalPercentage,
      getProgressClass,
      getPercentageClass
    };
  }, [attendanceData]);

  // Optimized styles with reduced animations and better performance
  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
    
    * {
      box-sizing: border-box;
    }
    
    body {
      margin: 0;
      padding: 0;
      font-family: 'Inter', sans-serif;
    }
    
    /* Reduced animations for better performance */
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    
    @keyframes slideIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    
    .container {
      background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #f8fafc;
      font-size: 1.125rem;
      font-weight: 500;
    }
    
    .main-content {
      margin: 0 auto;
      padding: 2rem;
      animation: slideIn 0.3s ease-out;
    }
    
    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 2rem;
    }
    
    .logo {
      font-size: 1.5rem;
      font-weight: 800;
      background: linear-gradient(135deg, #3b82f6, #8b5cf6);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    
    .header-buttons {
      display: flex;
      gap: 0.75rem;
    }
    
    .btn {
      padding: 0.75rem 1.5rem;
      border-radius: 12px;
      border: none;
      font-size: 0.875rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
      position: relative;
      overflow: hidden;
    }
    
    .btn:hover {
      transform: translateY(-1px);
    }
    
    .btn-primary {
      background: linear-gradient(135deg, #3b82f6, #2563eb);
      color: white;
      box-shadow: 0 4px 15px rgba(59, 130, 246, 0.4);
    }
    
    .btn-primary:hover {
      box-shadow: 0 6px 20px rgba(59, 130, 246, 0.5);
    }
    
    .btn-danger {
      background: linear-gradient(135deg, #ef4444, #dc2626);
      color: white;
      box-shadow: 0 4px 15px rgba(239, 68, 68, 0.4);
    }
    
    .btn-danger:hover {
      box-shadow: 0 6px 20px rgba(239, 68, 68, 0.5);
    }
    
    .dashboard {
      display: grid;
      grid-template-columns: 1fr 1.5fr;
      gap: 2rem;
    }
    
    .card {
      background: rgba(30, 41, 59, 0.9);
      backdrop-filter: blur(20px);
      border-radius: 24px;
      border: 1px solid rgba(148, 163, 184, 0.1);
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
      transition: transform 0.2s ease;
      position: relative;
      overflow: hidden;
      will-change: transform;
    }
    
    .card:hover {
      transform: translateY(-2px);
    }
    
    .profile-card {
      padding: 2rem;
      text-align: center;
    }
    
    .profile-avatar {
      width: 120px;
      height: 120px;
      border-radius: 50%;
      margin: 0 auto 1.5rem;
      position: relative;
      overflow: hidden;
      border: 4px solid rgba(59, 130, 246, 0.3);
      box-shadow: 0 0 30px rgba(59, 130, 246, 0.3);
    }
    
    .profile-avatar img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.2s ease;
    }
    
    .profile-avatar:hover img {
      transform: scale(1.05);
    }
    
    .profile-name {
      color: #f8fafc;
      font-weight: 800;
      font-size: 1.5rem;
      margin-bottom: 0.5rem;
      background: linear-gradient(135deg, #f8fafc, #cbd5e1);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    
    .profile-id {
      color: #64748b;
      font-size: 0.875rem;
      margin-bottom: 2rem;
      font-weight: 500;
    }
    
    .profile-details {
      border-top: 1px solid rgba(148, 163, 184, 0.1);
      padding-top: 1.5rem;
    }
    
    .detail-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1rem;
      padding: 0.75rem;
      border-radius: 12px;
      transition: background 0.2s ease;
    }
    
    .detail-row:hover {
      background: rgba(59, 130, 246, 0.1);
    }
    
    .detail-label {
      color: #94a3b8;
      font-size: 0.875rem;
      font-weight: 500;
    }
    
    .detail-value {
      color: #f8fafc;
      font-weight: 600;
      font-size: 0.875rem;
    }
    
    .attendance-card {
      padding: 2rem;
    }
    
    .card-title {
      color: #f8fafc;
      font-weight: 700;
      font-size: 1.5rem;
      margin-bottom: 2rem;
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }
    
    .card-title::before {
      content: '📊';
      font-size: 1.5rem;
    }
    
    .overall-attendance {
      margin-bottom: 2rem;
      padding: 1.5rem;
      background: rgba(59, 130, 246, 0.1);
      border-radius: 16px;
      border: 1px solid rgba(59, 130, 246, 0.2);
    }
    
    .attendance-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1rem;
    }
    
    .attendance-label {
      color: #cbd5e1;
      font-size: 1.125rem;
      font-weight: 600;
    }
    
    .attendance-percentage {
      padding: 0.5rem 1rem;
      border-radius: 20px;
      font-weight: 700;
      font-size: 1.125rem;
    }
    
    .percentage-high {
      background: linear-gradient(135deg, #10b981, #059669);
      color: white;
      box-shadow: 0 4px 15px rgba(16, 185, 129, 0.4);
    }
    
    .percentage-medium {
      background: linear-gradient(135deg, #f59e0b, #d97706);
      color: white;
      box-shadow: 0 4px 15px rgba(245, 158, 11, 0.4);
    }
    
    .percentage-low {
      background: linear-gradient(135deg, #ef4444, #dc2626);
      color: white;
      box-shadow: 0 4px 15px rgba(239, 68, 68, 0.4);
    }
    
    .progress-bar {
      width: 100%;
      height: 12px;
      background: rgba(55, 65, 81, 0.5);
      border-radius: 10px;
      overflow: hidden;
      position: relative;
    }
    
    .progress-fill {
      height: 100%;
      border-radius: 10px;
      transition: width 0.8s ease-out;
      position: relative;
      overflow: hidden;
    }
    
    .progress-high {
      background: linear-gradient(135deg, #10b981, #059669);
    }
    
    .progress-medium {
      background: linear-gradient(135deg, #f59e0b, #d97706);
    }
    
    .progress-low {
      background: linear-gradient(135deg, #ef4444, #dc2626);
    }
    
    .recent-sessions {
      margin-bottom: 2rem;
    }
    
    .section-title {
      color: #cbd5e1;
      font-weight: 600;
      font-size: 1.125rem;
      margin-bottom: 1rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
    
    .section-title::before {
      content: '📅';
      font-size: 1rem;
    }
    
    .session-row {
      display: flex;
      align-items: center;
      margin-bottom: 0.75rem;
      background: rgba(32, 40, 55, 0.6);
      border-radius: 12px;
      padding: 1rem;
      border: 1px solid rgba(148, 163, 184, 0.1);
      transition: background 0.2s ease;
    }
    
    .session-row:hover {
      background: rgba(32, 40, 55, 0.8);
      border-color: rgba(59, 130, 246, 0.2);
    }
    
    .session-date {
      color: #e2e8f0;
      font-size: 0.875rem;
      min-width: 110px;
      font-weight: 600;
    }
    
    .session-icons {
      display: flex;
      gap: 0.5rem;
      margin-left: 1rem;
    }
    
    .session-icon {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      transition: transform 0.2s ease;
      cursor: pointer;
    }
    
    .session-icon:hover {
      transform: scale(1.1);
    }
    
    .session-icon.present {
      background: linear-gradient(135deg, #10b981, #059669);
      color: white;
      box-shadow: 0 2px 10px rgba(16, 185, 129, 0.4);
    }
    
    .session-icon.absent {
      background: linear-gradient(135deg, #ef4444, #dc2626);
      color: white;
      box-shadow: 0 2px 10px rgba(239, 68, 68, 0.4);
    }
    
    .session-icon.neutral {
      background: linear-gradient(135deg, #6b7280, #4b5563);
      color: white;
      box-shadow: 0 2px 10px rgba(107, 114, 128, 0.4);
    }
    
    .summary-cards {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 1rem;
    }
    
    .summary-card {
      padding: 1.5rem;
      border-radius: 16px;
      text-align: center;
      transition: transform 0.2s ease;
      position: relative;
      overflow: hidden;
    }
    
    .summary-card:hover {
      transform: translateY(-2px);
    }
    
    .summary-present {
      background: linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(5, 150, 105, 0.2));
      border: 1px solid rgba(16, 185, 129, 0.3);
    }
    
    .summary-absent {
      background: linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(220, 38, 38, 0.2));
      border: 1px solid rgba(239, 68, 68, 0.3);
    }
    
    .summary-neutral {
      background: linear-gradient(135deg, rgba(107, 114, 128, 0.1), rgba(75, 85, 99, 0.2));
      border: 1px solid rgba(107, 114, 128, 0.3);
    }
    
    .summary-number {
      font-weight: 800;
      font-size: 2rem;
      margin-bottom: 0.5rem;
      background: linear-gradient(135deg, currentColor, currentColor);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    
    .summary-present .summary-number {
      color: #10b981;
    }
    
    .summary-absent .summary-number {
      color: #ef4444;
    }
    
    .summary-neutral .summary-number {
      color: #6b7280;
    }
    
    .summary-label {
      color: #94a3b8;
      font-weight: 600;
      font-size: 0.875rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    
    .no-data {
      color: #9ca3af;
      font-size: 0.875rem;
      text-align: center;
      padding: 2rem;
      background: rgba(32, 40, 55, 0.6);
      border-radius: 12px;
      border: 1px dashed rgba(148, 163, 184, 0.3);
    }
    
    .auth-container {
      background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 1rem;
    }
    
    .auth-card {
      background: rgba(30, 41, 59, 0.95);
      backdrop-filter: blur(20px);
      border-radius: 24px;
      padding: 3rem;
      text-align: center;
      max-width: 420px;
      width: 100%;
      border: 1px solid rgba(148, 163, 184, 0.1);
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    }
    
    .auth-icon {
      width: 80px;
      height: 80px;
      border-radius: 50%;
      background: linear-gradient(135deg, #3b82f6, #8b5cf6);
      margin: 0 auto 1.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 2rem;
      box-shadow: 0 10px 30px rgba(59, 130, 246, 0.3);
    }
    
    .auth-title {
      color: #f8fafc;
      font-size: 1.75rem;
      font-weight: 700;
      margin-bottom: 0.5rem;
      background: linear-gradient(135deg, #f8fafc, #cbd5e1);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    
    .auth-subtitle {
      color: #94a3b8;
      font-size: 1rem;
      margin-bottom: 2rem;
      font-weight: 500;
    }
    
    .pin-inputs {
      display: flex;
      justify-content: center;
      gap: 0.75rem;
      margin-bottom: 1.5rem;
    }
    
    .pin-input {
      width: 56px;
      height: 64px;
      border-radius: 16px;
      border: 2px solid rgba(55, 65, 81, 0.6);
      background: rgba(31, 41, 55, 0.8);
      color: #f8fafc;
      font-size: 1.25rem;
      font-weight: 700;
      text-align: center;
      outline: none;
      transition: all 0.2s ease;
      backdrop-filter: blur(10px);
    }
    
    .pin-input:focus {
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
      transform: scale(1.02);
    }
    
    .pin-input.error {
      border-color: #ef4444;
      box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.3);
    }
    
    .pin-error {
      color: #ef4444;
      font-size: 0.875rem;
      margin-bottom: 1rem;
      padding: 0.75rem;
      background: rgba(127, 29, 29, 0.3);
      border-radius: 12px;
      border: 1px solid rgba(239, 68, 68, 0.3);
    }
    
    .auth-demo {
      margin-top: 1.5rem;
      padding: 1rem;
      background: rgba(31, 41, 55, 0.6);
      border-radius: 12px;
      font-size: 0.875rem;
      color: #9ca3af;
      border: 1px solid rgba(55, 65, 81, 0.3);
    }
    
    .demo-pin {
      color: #f8fafc;
      font-weight: 700;
      font-family: 'Courier New', monospace;
    }
    
    .loading-container {
      background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #f8fafc;
      font-size: 1.125rem;
      font-weight: 500;
    }

     .navbar-logo {
        display: flex;
        align-items: center;
        gap: 10px;
      }
      
      .icon {
        font-size: 30px;
        font-weight: bold;
        color: #FF3B30;
        font-family: 'Poppins', sans-serif;
      }
      
      .logo-text h1 {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
        color: #FF3B30;
      }
      
      .logo-text p {
        margin: 0;
        font-size: 9px;
        letter-spacing: 0.5px;
        color: #FF3B30;
      }
    
    .loading-content {
      text-align: center;
    }
    
    .loading-spinner {
      width: 50px;
      height: 50px;
      border: 4px solid rgba(55, 65, 81, 0.3);
      border-top: 4px solid #3b82f6;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin: 0 auto 1rem;
    }
    
    .loading-spinner-mini {
      display: inline-block;
      width: 20px;
      height: 20px;
      border: 3px solid rgba(59, 130, 246, 0.2);
      border-top: 3px solid #3b82f6;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin-right: 8px;
      vertical-align: middle;
    }
    
    /* Responsive Design */
    @media (max-width: 1024px) {
      .dashboard {
        grid-template-columns: 1fr;
        gap: 1.5rem;
      }
      
      .main-content {
        padding: 1.5rem;
      }
      
      .header {
        flex-direction: column;
        gap: 1rem;
        align-items: stretch;
      }
      
      .header-buttons {
        justify-content: center;
      }
    }
    
    @media (max-width: 768px) {
      .main-content {
        padding: 1rem;
      }
      
      .auth-card {
        padding: 2rem;
        margin: 1rem;
      }
      
      .pin-inputs {
        gap: 0.5rem;
      }
      
      .pin-input {
        width: 48px;
        height: 56px;
        font-size: 1.125rem;
      }
      
      .summary-cards {
        grid-template-columns: 1fr;
        gap: 0.75rem;
      }
      
      .profile-avatar {
        width: 100px;
        height: 100px;
      }
      
      .profile-name {
        font-size: 1.25rem;
      }
      
      .card-title {
        font-size: 1.25rem;
      }
      
      .session-row {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.75rem;
      }
      
      .session-icons {
        margin-left: 0;
      }
    }
    
    @media (max-width: 480px) {
      .header-buttons {
        flex-direction: column;
      }
      
      .btn {
        width: 100%;
      }
      
      .pin-inputs {
        gap: 0.25rem;
      }
      
      .pin-input {
        width: 44px;
        height: 52px;
      }
    }
    
    /* Performance optimizations */
    .card, .btn, .session-row, .detail-row, .summary-card {
      will-change: transform;
    }
    
    /* Reduce motion for users who prefer it */
    @media (prefers-reduced-motion: reduce) {
      * {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
      }
    }
  `;

  useEffect(() => {
    if (pin.every(digit => digit !== '') && !buttonLoading && !pinAuthenticated) {
      validatePin();
    }
  }, [pin, buttonLoading, pinAuthenticated, validatePin]);

  // PIN Authentication Screen
  if (!pinAuthenticated && !showRegister) {
    return (
      <div className="auth-container">
        <style dangerouslySetInnerHTML={{ __html: styles }} />
        <div className="auth-card">
          <div className="auth-icon">🔒</div>
          <h2 className="auth-title">Enter PIN</h2>
          <p className="auth-subtitle">Please enter your 6-digit PIN to access attendance</p>
          <form onSubmit={handleSubmit}>
            <div className="pin-inputs">
             {pin.map((digit, index) => (
              <input
                key={index}
                id={`pin-${index}`}
                type="password"
                inputMode="numeric"
                maxLength="1"
                value={digit ? '●' : ''}
                onChange={(e) => handlePinChange(index, e.target.value.replace(/[^0-9]/g, ''))}
                onKeyDown={(e) => handlePinKeyDown(index, e)}
                onPaste={handlePinPaste}
                autoFocus={index === 0}
                className={`pin-input ${pinError ? 'error' : ''}`}
              />
            ))}
            </div>
            {pinError && (
              <div className="pin-error">
                {pinError}
              </div>
            )}
            <button type="submit" className="btn btn-primary" style={{ width: '100%', position: 'relative', minHeight: 44 }} disabled={buttonLoading}>
              {buttonLoading ? (
                <span className="button-spinner" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <span className="loading-spinner-mini" />
                </span>
              ) : (
                'Verify PIN'
              )}
            </button>
          </form>
          <div style={{ marginTop: 20 }}>
            <span style={{ color: '#94a3b8', fontSize: 14 }}>Don't have an account? </span>
            <button type="button" className="btn btn-secondary" style={{ marginLeft: 8, padding: '6px 16px', fontSize: 14 }} onClick={() => setShowRegister(true)}>
              Register
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Registration Screen
  if (!pinAuthenticated && showRegister) {
    return (
      <div className="auth-container">
        <style dangerouslySetInnerHTML={{ __html: styles }} />
        <div className="auth-card">
          <div className="auth-icon">📝</div>
          <h2 className="auth-title">Register</h2>
          <p className="auth-subtitle">Enter your mobile number and choose a 6-digit PIN</p>
          <form onSubmit={handleRegisterSubmit}>
            <input
              className="pin-input"
              style={{ width: '100%', marginBottom: 12 }}
              placeholder="Netra Mobile Number"
              value={registerData.mobilenumber}
              onChange={e => handleRegisterChange('mobilenumber', e.target.value.replace(/[^0-9]/g, ''))}
              autoFocus
            />
            <input
              className="pin-input"
              style={{ width: '100%', marginBottom: 12 }}
              placeholder="Netra Passoword"
              value={registerData.password}
              onChange={e => handleRegisterChange('password', e.target.value)}
              autoFocus
            />
    

            <div className="pin-inputs" style={{ marginBottom: 12 }}>
              {registerData.pin.map((digit, index) => (
                <input
                  key={index}
                  id={`pin-${index}`}
                  type="password"
                  inputMode="numeric"
                  maxLength="1"
                  value={digit}
                  onChange={e => handleRegisterChange('pin', { index, digit: e.target.value.replace(/[^0-9]/g, '') })}
                  onPaste={handleRegisterPinPaste}
                  onKeyDown={e => handlePinKeyDown(index, e)}
                  className="pin-input"
                  autoFocus={index === 0}
                  style={{ width: 44, height: 52 }}
                />
              ))}
            </div>
            {registerError && (
              <div className="pin-error">{registerError}</div>
            )}
            {registerSuccess && (
              <div className="pin-error" style={{ color: '#10b981', background: 'rgba(16,185,129,0.1)', borderColor: 'rgba(16,185,129,0.3)' }}>{registerSuccess}</div>
            )}
            <button type="submit" className="btn btn-primary" style={{ width: '100%', position: 'relative', minHeight: 44 }} disabled={registerLoading}>
              {registerLoading ? (
                <span className="button-spinner" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <span className="loading-spinner-mini" />
                </span>
              ) : (
                'Register'
              )}
            </button>
          </form>
          <div style={{ marginTop: 20 }}>
            <span style={{ color: '#94a3b8', fontSize: 14 }}>Already have an account? </span>
            <button type="button" className="btn btn-secondary" style={{ marginLeft: 8, padding: '6px 16px', fontSize: 14 }} onClick={() => setShowRegister(false)}>
              Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading Screen
  if (loading || !attendanceData) {
    return (
      <div className="loading-container">
        <style dangerouslySetInnerHTML={{ __html: styles }} />
        <div className="loading-content">
          <div className="loading-spinner"></div>
          Loading Data...
        </div>
      </div>
    );
  }
  const isMobile = window.innerWidth <= 768
  return (
    <div className="container">
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      <div className="main-content">
        {/* Header */}
        <div className="header">
           <div className="navbar-logo">
            <img src='/nav.png' style={{width:'40px'}}></img>
            <div className="logo-text" style={{width:'100px'}}>
              <h1>SPECTRONA</h1>
              <p>The Attendance App</p>
            </div>
          </div>
          <div 
  style={{
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '15px',
    width: '100%'
  }}
>
  
    <button 
   onClick={() => fetchAttendanceData()}
  className="btn btn-secondary"
  style={{
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    border: 'none',
    color: 'white',
    fontSize: '1rem',
    cursor: 'pointer',
    padding: '8px 12px',
    borderRadius: '10px',
    transition: 'background 0.3s, color 0.3s'
  }}
 
>
  Refresh Profile
</button>
  

 
    <button 
      onClick={handleLogout}
      className="btn btn-secondary"
      style={{
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
    border: 'none',
    color: 'white',
    fontSize: '1rem',
    cursor: 'pointer',
    padding: '8px 12px',
    borderRadius: '10px',
    transition: 'background 0.3s, color 0.3s'
      }}
    >
      Logout
    </button>
  
</div>
        </div>

        <div className="dashboard">
          {/* Profile Card */}
          <div className="card profile-card">
            <div className="profile-avatar">
             <img
                src={
                  profileData?.picture
                    ? `https://attendance-server-pi.vercel.app/proxy-image?url=${encodeURIComponent(profileData.picture)}`
                    : 'https://cdn-icons-png.flaticon.com/512/847/847969.png'
                }
                alt="Profile"
                loading="lazy"
                referrerPolicy="no-referrer"
                crossOrigin="anonymous"
                onError={(e) => {
                  console.error('Image load failed:', e);
                  e.target.onerror = null;
                  e.target.src = 'https://cdn-icons-png.flaticon.com/512/847/847969.png';
                }}
              />
            </div>
            <div className="profile-name">{profileData?.firstname}</div>
            <div className="profile-id">{profileData?.rollno}</div>
            <div className="profile-details">
              <div className="detail-row">
                <span className="detail-label">Department</span>
                <span className="detail-value">{profileData?.dept}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Section</span>
                <span className="detail-value">{profileData?.section}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Current Year</span>
                <span className="detail-value">{profileData?.currentyear}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Phone</span>
                <span className="detail-value">{profileData?.phone}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Year of Admission</span>
                <span className="detail-value">{profileData?.yearofadmision}</span>
              </div>
            </div>
          </div>

          {/* Attendance Card */}
          <div className="card attendance-card">
            <div className="card-title">Attendance Overview</div>

            {/* Overall Attendance */}
            <div className="overall-attendance">
              <div className="attendance-header">
                <span className="attendance-label">Overall Attendance</span>
                <span className={`attendance-percentage ${computedData.getPercentageClass(computedData.totalPercentage)}`}>
                  {parseFloat(computedData.totalPercentage).toFixed(2)}%
                </span>
              </div>
              <div className="progress-bar">
                <div 
                  className={`progress-fill ${computedData.getProgressClass(computedData.totalPercentage)}`}
                  style={{ width: `${Math.min(computedData.totalPercentage, 100)}%` }}
                ></div>
              </div>
            </div>

            {/* Recent Sessions */}
            <div className="recent-sessions">
              <div className="section-title">Recent Sessions</div>
              {computedData.dayObjects.length > 0 ? (
                computedData.dayObjects.slice(0, 12).map((day, idx) => (
                  <div key={`${day.date}-${idx}`} className="session-row">
                    <span className="session-date">{day.date}</span>
                    <div className="session-icons">
                      {Object.values(day.sessions || {}).map((val, i) => (
                        <span key={`${day.date}-${i}`}>{getSessionIcon(val)}</span>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-data">
                  No recent sessions available
                </div>
              )}
            </div>

            {/* Summary Cards */}
            <div className="summary-cards">
              <div className="summary-card summary-present">
                <div className="summary-number">{computedData.twoWeekSessions.present || 0}</div>
                <div className="summary-label">Present</div>
              </div>
              <div className="summary-card summary-absent">
                <div className="summary-number">{computedData.twoWeekSessions.absent || 0}</div>
                <div className="summary-label">Absent</div>
              </div>
              <div className="summary-card summary-neutral">
                <div className="summary-number">{computedData.twoWeekSessions.nosessions || 0}</div>
                <div className="summary-label">No Sessions</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
